import React, { ReactNode } from "react";
import { Col, Container, Row, Spinner, Alert, Button, Form } from "react-bootstrap";
import { Sun, Moon, BookHalf } from "react-bootstrap-icons";
import styled from "styled-components";

import { getOperationalAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { formatDate, getDaysAgo, getWidgetTheme } from "../../../utils/analytics";
import { MetricConfig, DEFAULT_FILTER_CONFIG, AnalyticsFilterConfig, AnalyticsFilters, DocsSidebarEntry } from "../../../types/analytics";
import { InteractiveLineChart, ExportButton, ScheduleExportModal, DocsSidebar, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";

/**
 * Ad Impressions page filter configuration
 * Uses standard presets (T/3/W/M/LM/Y) with start/end date and apply button
 */
const FILTER_CONFIG: AnalyticsFilterConfig = {
    ...DEFAULT_FILTER_CONFIG,
    datePresetStyle: "presets",
    defaultDatePreset: "W",
    showStartDate: true,
    showEndDate: true,
    showApplyButton: true
};

// Reference the config to avoid unused variable warning
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _filterConfig = FILTER_CONFIG;

const PageContainer = styled(Container)`
    position: relative;
`;

const SummaryBlocksContainer = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 25px;
    flex-wrap: wrap;
`;

const SummaryBlock = styled.div<{ color: string; bgcolor: string }>`
    background-color: ${props => props.bgcolor};
    padding: 15px 20px;
    border-radius: 5px;
    min-width: 180px;
    flex: 1;
    
    .value {
        font-size: 24px;
        font-weight: bold;
        color: ${props => props.color};
        margin-bottom: 5px;
    }
    
    .label {
        color: #666;
        font-size: 14px;
    }
`;

/* eslint-disable camelcase */
type AdImpressionsDataPoint = {
    datepartition: string;
    ad_complete: number;
    ad_start: number;
    ad_stop: number;
    plays: number;
    views: number;
    pct_plays_from_views: number;
    pct_complete_of_started: number;
    customer: string;
    generated_at: string;
};
/* eslint-enable camelcase */

type AdImpressionsProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type AdImpressionsState = {
    analyticsData: AdImpressionsDataPoint[];
    isLoading: boolean;
    error: string | null;
    startDate: string;
    endDate: string;
    // For filter UI state
    tempStartDate: string;
    tempEndDate: string;
    isDarkTheme: boolean;
    selectedPreset: string;
    isScrolled: boolean;
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

class AdImpressions extends React.Component<AdImpressionsProps, AdImpressionsState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: AdImpressionsProps) {
        super(props);
        
        const today = new Date();
        const oneWeekAgo = getDaysAgo(7);
        const startDate = formatDate(oneWeekAgo);
        const endDate = formatDate(today);
        
        /* eslint-disable camelcase */
        this.state = {
            analyticsData: [],
            isLoading: true,
            error: null,
            startDate,
            endDate,
            tempStartDate: startDate,
            tempEndDate: endDate,
            isDarkTheme: true,
            selectedPreset: "W",
            isScrolled: false,
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
        /* eslint-enable camelcase */
    }

    componentDidMount(): void {
        this.fetchData();
        window.addEventListener("scroll", this.handleScroll);
    }

    componentWillUnmount(): void {
        window.removeEventListener("scroll", this.handleScroll);
    }

    private handleScroll = (): void => {
        this.setState({ isScrolled: window.scrollY > 10 });
    };

    private handleDatePreset = (preset: string): void => {
        const today = new Date();
        let startDate: Date;
        let endDate: Date = today;

        switch (preset) {
            case "T": // Today
                startDate = today;
                break;
            case "3": // Last 3 days
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 2);
                break;
            case "W": // Week
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 6);
                break;
            case "M": // Month
                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 1);
                break;
            case "LM": // Last Month
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "Y": // Year
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 365);
                break;
            default:
                return;
        }

        this.setState({
            tempStartDate: formatDate(startDate),
            tempEndDate: formatDate(endDate),
            selectedPreset: preset
        });
    };

    private fetchData = async (): Promise<void> => {
        try {
            this.setState({ isLoading: true, error: null });
            
            const response = await getOperationalAnalyticsWidget(
                "ad_impressions",
                this.state.startDate,
                this.state.endDate
            );
            
            const data = (response as any)?.result;
            const dataArray = Array.isArray(data) ? data : [];
            this.setState({ analyticsData: dataArray });

            const meta = (response as any)?.widget;
            /* eslint-disable camelcase */
            const newEntry: DocsSidebarEntry | null = meta ? {
                widgetKey: "ad_impressions",
                displayName: meta.display_name,
                description: meta.description,
                schema: (response as any)?.schema
            } : null;
            /* eslint-enable camelcase */
            const sidebarEntries: DocsSidebarEntry[] = [newEntry].filter((e): e is DocsSidebarEntry => e !== null);
            this.setState({ sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching ad impressions data", err);
            this.setState({ error: "Failed to fetch ad impressions data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private handleApplyFilters = (): void => {
        this.setState({
            startDate: this.state.tempStartDate,
            endDate: this.state.tempEndDate
        }, () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { startDate, endDate } = this.state;
        return {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };
    };

    private getExportData = (): ExportableData[] => {
        const { analyticsData } = this.state;

        if (analyticsData.length === 0) {
            return [];
        }

        /* eslint-disable camelcase */
        return [{
            title: "Ad Impressions",
            columns: [
                { key: "datepartition", label: "Date", format: "date" },
                { key: "ad_start", label: "Ad Starts", format: "number" },
                { key: "ad_complete", label: "Ad Completes", format: "number" },
                { key: "ad_stop", label: "Ad Stops", format: "number" },
                { key: "plays", label: "Plays", format: "number" },
                { key: "views", label: "Views", format: "number" },
                { key: "pct_plays_from_views", label: "% Plays from Views", format: "percent" },
                { key: "pct_complete_of_started", label: "% Complete of Started", format: "percent" }
            ],
            rows: analyticsData,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "ad_impressions",
                customerSlug: this.props.customerConfig.slug
            }
        }];
        /* eslint-enable camelcase */
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Ad Impressions",
            pageKey: "ad_impressions",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private renderSummaryBlocks(): ReactNode {
        const { analyticsData, isDarkTheme } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        
        if (analyticsData.length === 0) {
            return null;
        }
        
        const totalPlays = analyticsData.reduce((sum, d) => sum + d.plays, 0);
        const totalAdCompletes = analyticsData.reduce((sum, d) => sum + d.ad_complete, 0);
        const totalAdStarts = analyticsData.reduce((sum, d) => sum + d.ad_start, 0);
        const adsStartedPct = totalAdStarts > 0 
            ? ((totalAdCompletes / totalAdStarts) * 100).toFixed(1) 
            : "0.0";
        
        return (
            <SummaryBlocksContainer>
                <SummaryBlock color="#8884d8" bgcolor={theme.chartBg}>
                    <div className="value">{totalPlays.toLocaleString()}</div>
                    <div className="label">NUMBER OF PLAYS</div>
                </SummaryBlock>
                <SummaryBlock color="#82ca9d" bgcolor={theme.chartBg}>
                    <div className="value">{totalAdCompletes.toLocaleString()}</div>
                    <div className="label">AD COMPLETES</div>
                </SummaryBlock>
                <SummaryBlock color="#ffc658" bgcolor={theme.chartBg}>
                    <div className="value">{adsStartedPct}%</div>
                    <div className="label">ADS STARTED %</div>
                </SummaryBlock>
            </SummaryBlocksContainer>
        );
    }

    private renderChart(): ReactNode {
        const { analyticsData, isDarkTheme } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const description = this.state.sidebarEntries.find((e: any) => e.widgetKey === "ad_impressions")?.description;

        /* eslint-disable camelcase */
        const adMetricConfig: MetricConfig[] = [
            { key: "ad_complete", name: "Ad Complete", color: "#82ca9d" },
            { key: "ad_start", name: "Ad Start", color: "#8884d8" },
            { key: "ad_stop", name: "Ad Stop", color: "#ffc658" }
        ];

        const percentageMetricConfig: MetricConfig[] = [
            { key: "pct_plays_from_views", name: "% Plays from Views", color: "#ff6b6b" },
            { key: "pct_complete_of_started", name: "% Complete of Started", color: "#9333ea" }
        ];

        // Transform data for chart
        const chartData = analyticsData.map(d => ({
            date: d.datepartition,
            ad_complete: d.ad_complete,
            ad_start: d.ad_start,
            ad_stop: d.ad_stop,
            plays: d.plays,
            views: d.views,
            pct_plays_from_views: d.pct_plays_from_views,
            pct_complete_of_started: d.pct_complete_of_started
        }));
        /* eslint-enable camelcase */

        return (
            <>
                <WidgetCard
                    title="Ad Engagement Metrics"
                    description={description}
                    theme={theme}
                    isEmpty={analyticsData.length === 0}
                    emptyMessage="No data available for the selected date range."
                >
                    <InteractiveLineChart
                        title=""
                        data={chartData}
                        xAxisDataKey="date"
                        metrics={adMetricConfig}
                        theme={theme}
                        yAxisLabel="Count"
                        tooltipValueFormatter={(value: number) => value.toLocaleString()}
                    />
                </WidgetCard>

                <WidgetCard
                    title="Conversion Percentages"
                    description={description}
                    theme={theme}
                    isEmpty={analyticsData.length === 0}
                    emptyMessage="No data available for the selected date range."
                >
                    <InteractiveLineChart
                        title=""
                        data={chartData}
                        xAxisDataKey="date"
                        metrics={percentageMetricConfig}
                        theme={theme}
                        yAxisLabel="Percentage (%)"
                        yAxisDomain={[0, 100]}
                        tooltipValueFormatter={(value: number) => `${Number(value).toFixed(1)}%`}
                    />
                </WidgetCard>
            </>
        );
    }

    private hasChanges = (): boolean => {
        const { startDate, endDate, tempStartDate, tempEndDate } = this.state;
        return startDate !== tempStartDate || endDate !== tempEndDate;
    };

    public render(): ReactNode {
        const { isLoading, error, tempStartDate, tempEndDate, isDarkTheme, selectedPreset, isScrolled, sidebarOpen, sidebarEntries } = this.state;
        const hasChanges = this.hasChanges();
        const datePresets = ["T", "3", "W", "M", "LM", "Y"];

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                <PageContainer className="mw-100">
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.analyticsData.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                        <Button
                            onClick={this.toggleSidebar}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Ad Impressions</h2>
                        </Col>
                    </Row>

                    <div
                        className={isScrolled ? "bg-primary-transparent" : ""}
                        style={{
                            position: "sticky",
                            top: 56,
                            zIndex: 100,
                            backgroundColor: isScrolled ? undefined : "transparent",
                            paddingTop: "12px",
                            paddingBottom: "8px",
                            marginBottom: "2px",
                            marginLeft: "-15px",
                            marginRight: "-15px",
                            paddingLeft: "15px",
                            paddingRight: "15px",
                            width: "calc(100% + 30px)",
                            transition: "background-color 0.2s ease"
                        }}
                    >
                        <Row className="align-items-start">
                            <Col md={3}>
                                <Form.Group>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                                        <Form.Label style={{ marginBottom: 0 }}>Start Date</Form.Label>
                                        <div style={{ display: "flex", gap: "4px" }}>
                                            {datePresets.map(preset => (
                                                <Button
                                                    key={preset}
                                                    size="sm"
                                                    variant={selectedPreset === preset ? "secondary" : "outline-secondary"}
                                                    onClick={() => this.handleDatePreset(preset)}
                                                    style={{
                                                        padding: "0px 5px",
                                                        fontSize: "10px",
                                                        lineHeight: "1.2",
                                                        height: "20px",
                                                        minWidth: "20px",
                                                        ...(selectedPreset !== preset ? {
                                                            backgroundColor: "#e9ecef",
                                                            borderColor: "#ced4da",
                                                            color: "#6c757d"
                                                        } : {})
                                                    }}
                                                    title={
                                                        preset === "T" ? "Today" :
                                                        preset === "3" ? "Last 3 Days" :
                                                        preset === "W" ? "Week" :
                                                        preset === "M" ? "Month" :
                                                        preset === "LM" ? "Last Month" :
                                                        "Year"
                                                    }
                                                >
                                                    {preset}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                    <Form.Control
                                        type="date"
                                        value={tempStartDate}
                                        onChange={(e) => this.setState({ tempStartDate: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={{ marginBottom: "4px" }}>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={tempEndDate}
                                        onChange={(e) => this.setState({ tempEndDate: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label style={{ marginBottom: "4px" }}>&nbsp;</Form.Label>
                                    <Button
                                        variant={hasChanges ? "secondary" : "outline-secondary"}
                                        onClick={this.handleApplyFilters}
                                        disabled={!hasChanges || isLoading}
                                        style={{
                                            width: "100%",
                                            display: "block",
                                            ...(!hasChanges && {
                                                backgroundColor: "#e9ecef",
                                                borderColor: "#ced4da",
                                                color: "#6c757d",
                                                cursor: "not-allowed"
                                            })
                                        }}
                                    >
                                        Apply Filters
                                    </Button>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                {isLoading && (
                    <Row>
                        <Col className="text-center" style={{ padding: "40px" }}>
                            <Spinner animation="border" role="status">
                                <span className="sr-only">Loading...</span>
                            </Spinner>
                        </Col>
                    </Row>
                )}

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}

                {!isLoading && !error && (
                    <>
                        <Row>
                            <Col>
                                {this.renderSummaryBlocks()}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {this.renderChart()}
                            </Col>
                        </Row>
                    </>
                )}

                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={this.convertToAnalyticsFilters()}
                        pageKey="ad_impressions"
                        pageTitle="Ad Impressions"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </PageContainer>
                </div>
                <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default AdImpressions;
